import { Link } from "react-router-dom";
import { MapPin, Phone, Mail } from "lucide-react";
import { CONTACT, SOCIALS, BOOKING_URL, SERVICE_AREAS } from "../data/site";
import { track } from "../lib/analytics";
import logoMark from "../assets/zentryx-mark.png";

const COMPANY = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const SERVICES = [
  "Managed IT Services", "Cybersecurity Services", "Microsoft 365 Support",
  "Cloud Services", "Backup & Disaster Recovery", "Network Support & Monitoring",
  "IT Consulting / Strategy",
];

const INDUSTRIES = [
  { label: "IT for CPA & Accounting Firms", to: "/industries/cpa-accounting-firms-atlanta" },
  { label: "IT for Law Firms", to: "/industries/law-firms" },
  { label: "IT for Healthcare Practices", to: "/industries/healthcare-practices" },
  { label: "IT for Architecture Firms", to: "/industries/architecture-engineering-firms-atlanta" },
  { label: "IT for Engineering Firms", to: "/industries/managed-it-for-engineering-firms-atlanta" },
  { label: "IT for Professional Services", to: "/industries/professional-services" },
  { label: "IT for Financial Services", to: "/industries/financial-services" },
];

const RESOURCES = [
  { label: "How to Choose the Best MSP in Atlanta", to: "/resources/how-to-choose-best-msp-atlanta" },
  { label: "What Atlanta CPA Firms Should Look for", to: "/resources/what-atlanta-cpa-firms-should-look-for-in-an-msp" },
  { label: "What Architecture Firms Should Look For", to: "/resources/what-architecture-firms-should-look-for-in-an-it-partner" },
  { label: "Managed IT vs Break-Fix Support", to: "/resources/managed-it-vs-break-fix-atlanta" },
  { label: "View All Resources", to: "/resources" },
];

export const Footer = () => (
  <footer data-testid="footer" className="bg-navy-dark text-slate-300">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
      <div className="lg:col-span-1">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center p-1.5">
            <img src={logoMark} alt="Zentryx Systems" className="h-full w-auto" />
          </div>
          <span className="font-heading font-extrabold text-lg text-white">Zentryx Systems</span>
        </div>
        <p className="text-sm leading-relaxed text-slate-400">
          Atlanta-based managed IT and cybersecurity for small and midsize businesses across every industry — from CPA and accounting to law, healthcare, architecture, engineering, and professional services.
        </p>
        <div className="flex flex-wrap gap-2.5 mt-6">
          {SOCIALS.map((s) => (
            <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
              data-testid={`social-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              aria-label={s.label} title={s.label}
              className="h-9 w-9 rounded-full bg-white/10 hover:bg-emerald flex items-center justify-center transition-colors">
              <s.icon className="h-4 w-4 text-white" />
            </a>
          ))}
        </div>
      </div>

      <div>
        <h4 className="text-white font-heading font-semibold text-sm uppercase tracking-widest mb-4">Company</h4>
        <ul className="space-y-2.5 text-sm">
          {COMPANY.map((l) => (
            <li key={l.to}><Link to={l.to} className="hover:text-white transition-colors">{l.label}</Link></li>
          ))}
          <li>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
              onClick={() => track("book_intro_call_click", { location: "footer" })}
              className="hover:text-white transition-colors">Book an Intro Call</a>
          </li>
        </ul>
      </div>

      <div>
        <h4 className="text-white font-heading font-semibold text-sm uppercase tracking-widest mb-4">Services</h4>
        <ul className="space-y-2.5 text-sm">
          {SERVICES.map((s) => (
            <li key={s}><Link to="/services" className="hover:text-white transition-colors">{s}</Link></li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="text-white font-heading font-semibold text-sm uppercase tracking-widest mb-4">Industries</h4>
        <ul className="space-y-2.5 text-sm">
          {INDUSTRIES.map((l) => (
            <li key={l.to}><Link to={l.to} className="hover:text-white transition-colors">{l.label}</Link></li>
          ))}
        </ul>
        <h4 className="text-white font-heading font-semibold text-sm uppercase tracking-widest mb-4 mt-6">Resources</h4>
        <ul className="space-y-2.5 text-sm">
          {RESOURCES.map((l) => (
            <li key={l.to}><Link to={l.to} className="hover:text-white transition-colors">{l.label}</Link></li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="text-white font-heading font-semibold text-sm uppercase tracking-widest mb-4">Contact</h4>
        <ul className="space-y-3 text-sm">
          <li className="flex items-start gap-3"><MapPin className="h-4 w-4 mt-0.5 text-emerald" /> {CONTACT.area}</li>
          <li className="flex items-center gap-3"><Phone className="h-4 w-4 text-emerald" /><a href={CONTACT.phoneHref} data-testid="footer-phone" className="hover:text-white transition-colors">{CONTACT.phone}</a></li>
          <li className="flex items-center gap-3"><Mail className="h-4 w-4 text-emerald" /><a href={CONTACT.emailHref} data-testid="footer-email" className="hover:text-white transition-colors break-all">{CONTACT.email}</a></li>
        </ul>
        <p className="mt-5 text-sm text-slate-400 italic">{CONTACT.tagline}</p>
      </div>
    </div>

    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6">
        <p className="text-xs text-slate-400 leading-relaxed">
          <span className="text-slate-300 font-semibold">Service areas:</span> {SERVICE_AREAS.join(", ")}.
        </p>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
        <span>© {new Date().getFullYear()} Zentryx Systems. All rights reserved.</span>
        <span>Managed IT &amp; Cybersecurity · Atlanta, GA</span>
      </div>
    </div>
  </footer>
);
